import { Box } from "@mui/material";
import { httpClient } from "../../common/httpclient";
import { formatPhone } from "../../common/formatters";
import { useState, useEffect, useMemo } from "react";
import { KitField, KitInput } from "../../component/common/MobileKitExtras";
import { protoIcon } from "../../component/common/ProtoIcon";
import Dropdown from "../../component/dropdown/Dropdown";
import NewCustomer from "../../component/NewCustomer";
import ModalAdd from "../../component/vehicle/ModalAdd";
import { useTheme } from '@mui/material/styles';
import { Plus } from 'lucide-react';

// Campo padrão "combobox / search-and-add" (DESIGN-SYSTEM.md §5.5) — mesma
// caixa `ff-input` (52px, radius 12px, fundo `background.subtle`, label
// flutuante) usada nos demais campos do formulário enxuto de
// `prototipo-ordem-servico.html` (`view-nova`). Cópia local do mesmo padrão
// já estabelecido em `pages/products/Form.js` (`standaloneFieldSx` /
// `standaloneInputBoxSx`) — ainda não extraído para um util compartilhado
// (ver comentário equivalente em `theme.js` MuiInputLabel).
const ffFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: 'background.subtle',
        height: '52px',
    },
    '& .MuiOutlinedInput-input:not(.MuiSelect-select)': {
        padding: '21px 14px 7px 14px',
        fontSize: '13.5px',
    },
    // Veículo usa label flutuante (não static-micro): reserva o topo do
    // valor como no `<input>` (21px) e mantém 32px à direita p/ o ícone.
    '& .MuiSelect-select.MuiOutlinedInput-input': {
        padding: '21px 32px 7px 14px',
        display: 'block',
        fontSize: '13.5px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: '1.5px',
        borderColor: 'divider',
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
        display: 'none',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: '1.5px',
    },
    '& .MuiInputLabel-root': {
        fontSize: '13.5px',
        color: 'text.disabled',
        transform: 'translate(14px, 17px) scale(1)',
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
        fontSize: '10px',
        fontWeight: 800,
        color: 'primary.main',
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        transform: 'translate(14px, 8px) scale(1)',
    },
};

// `.new-inline` do protótipo — link discreto abaixo do campo (não o botão
// "+" inline do `Dropdown` usado em outras telas) para abrir o cadastro
// rápido de cliente/veículo.
const NewInlineLink = ({ label, onClick, disabled }) => (
    <Box
        onClick={disabled ? undefined : onClick}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            mt: '6px',
            fontSize: '11.5px',
            fontWeight: 700,
            color: disabled ? 'text.disabled' : 'primary.main',
            cursor: disabled ? 'default' : 'pointer',
            '&:hover': disabled ? {} : { opacity: 0.75 },
        }}
    >
        <Plus size={12} strokeWidth={2.5} />
        {label}
    </Box>
);

const requiredFieldLabel = (text) => (
    <>
        {text} <Box component="span" sx={{ color: 'error.main' }}>*</Box>
    </>
);

const UsersIcon = protoIcon('users');
const CarIcon = protoIcon('car');
const PlusIcon = protoIcon('plus');

/**
 * Campo "Cliente" do shell mobile (`prototipos-mobile/ordens/nova.html`): `.input-ico.right`
 * (ícone de clientes à esquerda, botão "+" à direita) com busca por nome; a lista de
 * resultados aparece logo abaixo do campo. Mesmos dados/seleção do combobox do desktop.
 */
function MobileLookupField({ label, required, Icon, placeholder, newLabel, emptyText, items, value, onSelect, onNew, disabled, error, helperText }) {
    // items: [{ id, name, sub }]
    const selected = useMemo(() => items.find((c) => String(c.id) === String(value)) || null, [items, value]);
    const [text, setText] = useState('');
    const [open, setOpen] = useState(false);

    // Sincroniza o texto com o item selecionado (inclusive o recém-cadastrado);
    // sem seleção (ex.: troca de cliente), limpa o texto residual se o campo não está em edição.
    useEffect(() => {
        if (selected) setText(selected.name || '');
        else if (!open) setText('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected?.id, selected?.name]);

    const term = text.trim().toLowerCase();
    const options = useMemo(() => {
        const base = selected && term === String(selected.name || '').toLowerCase() ? items : items.filter((c) => `${c.name || ''} ${c.sub || ''}`.toLowerCase().includes(term));
        return base.slice(0, 40);
    }, [items, term, selected]);

    return (
        <KitField label={label} required={required} help={helperText} error={error}>
            <Box sx={{ position: 'relative' }}>
                <Box component="span" sx={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'text.disabled', display: 'inline-flex', pointerEvents: 'none' }}>
                    <Icon size={20} />
                </Box>
                <KitInput
                    value={text}
                    error={error}
                    disabled={disabled}
                    aria-label={label}
                    autoComplete="off"
                    placeholder={placeholder}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 150)}
                    onChange={(e) => {
                        setText(e.target.value);
                        setOpen(true);
                        if (selected) onSelect('');
                    }}
                    sx={{ pl: '44px', pr: '48px' }}
                />
                <Box
                    component="button"
                    type="button"
                    aria-label={newLabel}
                    disabled={disabled}
                    onClick={onNew}
                    sx={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', width: 38, height: 38, borderRadius: '10px', border: 0, background: 'none', p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: disabled ? 'text.disabled' : 'primary.main', cursor: disabled ? 'default' : 'pointer' }}
                >
                    <PlusIcon size={20} />
                </Box>
                {open && (
                    <Box
                        onMouseDown={(e) => e.preventDefault()}
                        sx={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 6px)', zIndex: 5, maxHeight: 240, overflowY: 'auto', backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: '14px', boxShadow: '0 6px 20px rgba(15,23,42,.08)' }}
                    >
                        {options.length === 0 ? (
                            <Box sx={{ p: '12px 14px', fontSize: 13, color: 'text.disabled' }}>{emptyText}</Box>
                        ) : options.map((c) => (
                            <Box
                                key={c.id}
                                component="button"
                                type="button"
                                onClick={() => {
                                    onSelect(c.id);
                                    setText(c.name || '');
                                    setOpen(false);
                                }}
                                sx={{ display: 'block', width: '100%', textAlign: 'left', border: 0, borderBottom: '1px solid', borderColor: 'divider', background: 'none', p: '10px 14px', minHeight: 48, fontFamily: 'inherit', color: 'text.primary', cursor: 'pointer', '&:last-child': { borderBottom: 0 }, '&:active': { backgroundColor: 'background.subtle' } }}
                            >
                                <Box sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.4 }}>{c.name}</Box>
                                {c.sub && <Box sx={{ fontSize: 12, color: 'text.disabled', lineHeight: 1.4 }}>{c.sub}</Box>}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </KitField>
    );
}

export default function SelectorClient({ formik, selectedCustomer, handleChange, selectedVehicle, handleVehicleChange, useVehicle, onSelectionInfoChange, mobile = false }) {
    const theme = useTheme();
    const [customers, setCustomers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [openDialogNewCustomer, setOpenDialogNewCustomer] = useState(false);
    const [openDialogNewVehicle, setOpenDialogNewVehicle] = useState(false);
    const [selectedCustomerData, setSelectedCustomerData] = useState(null);
    const [pendingVehicleId, setPendingVehicleId] = useState(null);

    useEffect(() => {
        httpClient.get("/customers").then((response) => {
            setCustomers(response.data);
        }).catch((error) => {
            console.error('Erro ao carregar clientes:', error);
        });
    }, []);

    useEffect(() => {
        if (selectedCustomer) {
            // Buscar dados do cliente selecionado
            const customer = customers.find(c => c.ID === selectedCustomer);
            setSelectedCustomerData(customer);

            // Buscar veículos do cliente
            httpClient.get(`/vehicles/customer/${selectedCustomer}`).then((response) => {
                setVehicles(response.data);
            }).catch((error) => {
                console.error('Erro ao carregar veículos:', error);
                setVehicles([]);
            });
        } else {
            setVehicles([]);
            setSelectedCustomerData(null);
            // Limpa o veículo pendente quando não há cliente selecionado
            setPendingVehicleId(null);
        }
    }, [selectedCustomer, customers]);

    // Notifica o pai (opcional, usado pelo preview ao vivo de `Genereate.js`
    // — DESIGN-SYSTEM.md §5.5 "Accordion insertion form") com os dados de
    // exibição do cliente/veículo selecionados. Não altera nenhum
    // comportamento existente: os demais consumidores deste componente
    // (`orderData.js`, usado nos drawers de edição de Operator.js/View.js)
    // simplesmente não passam essa prop.
    useEffect(() => {
        if (!onSelectionInfoChange) return;
        const vehicleId = formik?.values?.vehicle_id;
        const vehicle = vehicles.find(v => {
            const vId = v.ID || v.Id || v.id;
            return String(vId) === String(vehicleId);
        });
        onSelectionInfoChange({
            customerName: selectedCustomerData?.Name || '',
            customerPhone: selectedCustomerData?.Cellphone ? formatPhone(selectedCustomerData.Cellphone) : '',
            vehicleLabel: vehicle ? `${vehicle.Manufacturer} - ${vehicle.Model}${vehicle.LicensePlate ? ` — ${vehicle.LicensePlate}` : ''}` : '',
            // KM atual cadastrado do veículo selecionado (null se não houver veículo).
            vehicleKilometer: vehicle ? (Number(vehicle.Kilometer) || 0) : null,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCustomerData, vehicles, formik?.values?.vehicle_id, onSelectionInfoChange]);

    // Efeito para selecionar automaticamente o veículo quando ele for adicionado à lista
    useEffect(() => {
        if (pendingVehicleId && vehicles.length > 0) {
            // Verifica se o veículo está na lista
            const vehicleExists = vehicles.some(v => {
                const vehicleId = v.ID || v.Id || v.id;
                return String(vehicleId) === String(pendingVehicleId);
            });

            if (vehicleExists) {
                // Converte o ID para string para garantir compatibilidade
                const vehicleIdStr = String(pendingVehicleId);
                handleVehicleChange({ target: { value: vehicleIdStr } });
                setPendingVehicleId(null);
            }
        }
    }, [vehicles, pendingVehicleId, handleVehicleChange]);

    const handleOnSuccess = (customer) => {
        setCustomers([...customers, customer]);
        // O ID pode vir como id, ID ou Id
        const customerId = customer.ID || customer.Id || customer.id;
        if (customerId) {
            handleChange(customerId);
        }
    }

    const handleOnSuccessVehicle = async (vehicle) => {
        // Recarrega a lista de veículos para garantir dados atualizados
        if (selectedCustomer) {
            // O endpoint retorna {id: "uuid"}, então extraímos o id
            const vehicleId = vehicle.id || vehicle.ID || vehicle.Id;

            if (vehicleId) {
                // Define o ID do veículo que deve ser selecionado
                setPendingVehicleId(String(vehicleId));

                try {
                    // Aguarda um pouco para garantir que o backend processou o cadastro
                    await new Promise(resolve => setTimeout(resolve, 300));

                    const response = await httpClient.get(`/vehicles/customer/${selectedCustomer}`);
                    const updatedVehicles = response.data || [];
                    setVehicles(updatedVehicles);

                    // Tenta selecionar o veículo imediatamente se estiver na lista
                    const vehicleInList = updatedVehicles.find(v => {
                        const vId = String(v.ID || v.Id || v.id);
                        return vId === String(vehicleId);
                    });

                    if (vehicleInList) {
                        handleVehicleChange({ target: { value: String(vehicleId) } });
                        setPendingVehicleId(null);
                    }
                } catch (error) {
                    console.error('Erro ao recarregar veículos:', error);
                    // Fallback: tenta selecionar mesmo com erro
                    if (vehicleId) {
                        handleVehicleChange({ target: { value: String(vehicleId) } });
                    }
                }
            }
        }
        setOpenDialogNewVehicle(false);
    }

    const getCustomerName = () => {
        if (!selectedCustomerData) return '';
        return selectedCustomerData.Name || '';
    }

    // Linha de opção do combo de cliente: nome + telefone como linha
    // secundária (`.combo-opt`/`.oname`/`.osub` do protótipo).
    const renderClientOption = (props, option) => (
        <li {...props} key={option.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '10px 12px' }}>
            <span style={{ fontWeight: 600, fontSize: 13, color: theme.palette.text.primary }}>{option.name}</span>
            {option.sub && <span style={{ fontSize: 11, color: theme.palette.text.disabled }}>{option.sub}</span>}
        </li>
    );

    // Shell mobile (`ordens/nova.html`): Cliente (input com "+") e Veículo (select) empilhados.
    if (mobile) {
        const vehicleError = formik.touched.vehicle_id && Boolean(formik.errors.vehicle_id);
        const customerItems = customers.map((c) => ({ id: c.ID, name: c.Name, sub: c.Cellphone ? formatPhone(c.Cellphone) : '' }));
        const vehicleItems = vehicles.map((v) => ({
            id: v.ID || v.Id || v.id,
            name: `${v.Manufacturer} ${v.Model}`,
            sub: v.LicensePlate || '',
        }));
        return (
            <Box>
                <MobileLookupField
                    label="Cliente"
                    required
                    Icon={UsersIcon}
                    placeholder="Buscar cliente…"
                    newLabel="Novo cliente"
                    emptyText="Nenhum cliente encontrado"
                    items={customerItems}
                    value={formik.values.customer_id}
                    onSelect={handleChange}
                    onNew={() => setOpenDialogNewCustomer(true)}
                    error={formik.touched.customer_id && Boolean(formik.errors.customer_id)}
                    helperText={formik.touched.customer_id && formik.errors.customer_id}
                />
                {useVehicle && (
                    <MobileLookupField
                        label="Veículo"
                        Icon={CarIcon}
                        placeholder="Buscar veículo…"
                        newLabel="Novo veículo"
                        emptyText="Nenhum veículo encontrado"
                        items={vehicleItems}
                        value={formik.values.vehicle_id || ''}
                        disabled={!selectedCustomer}
                        onSelect={(id) => handleVehicleChange({ target: { value: id } })}
                        onNew={() => {
                            if (selectedCustomer) setOpenDialogNewVehicle(true);
                        }}
                        error={vehicleError}
                        helperText={vehicleError ? formik.errors.vehicle_id : (!selectedCustomer ? 'Selecione um cliente para ver os veículos disponíveis' : '')}
                    />
                )}

                <NewCustomer
                    open={openDialogNewCustomer}
                    setOpen={setOpenDialogNewCustomer}
                    onSuccess={(data) => {
                        handleOnSuccess(data);
                        setOpenDialogNewCustomer(false);
                    }}
                />
                <ModalAdd
                    open={openDialogNewVehicle}
                    onClose={() => setOpenDialogNewVehicle(false)}
                    customerId={selectedCustomer}
                    customerName={getCustomerName()}
                    onSuccess={handleOnSuccessVehicle}
                />
            </Box>
        );
    }

    return (
        // Grid de 2 colunas fixas mantém Cliente/Veículo sempre na mesma
        // linha (mesmo padrão usado nos date-pickers em orderData.js),
        // em vez de depender de flex-wrap baseado na largura do container.
        // Sem veículo, o Cliente ocupa a linha inteira (1 coluna).
        <Box sx={{ display: 'grid', gridTemplateColumns: useVehicle ? '1fr 1fr' : '1fr', gap: 2, alignItems: 'flex-start' }}>
            {/* Cliente — combobox de busca-e-anexar (§5.5) */}
            <Box sx={{ minWidth: 0 }}>
                <Dropdown
                    mode="autocomplete"
                    variant="outlined"
                    size="medium"
                    sx={ffFieldSx}
                    label={requiredFieldLabel('Cliente')}
                    data={customers.map(customer => ({
                        id: customer.ID,
                        name: customer.Name,
                        sub: customer.Cellphone ? formatPhone(customer.Cellphone) : '',
                    })) || []}
                    selectedItem={formik.values.customer_id}
                    handleChange={handleChange}
                    error={formik.touched.customer_id && Boolean(formik.errors.customer_id)}
                    helperText={formik.touched.customer_id && formik.errors.customer_id}
                    renderOption={renderClientOption}
                />
                <NewInlineLink label="Novo cliente" onClick={() => setOpenDialogNewCustomer(true)} />
            </Box>

            {/* Veículo — select de rótulo estático (`.ffs`), habilitado só após escolher o cliente */}
            {useVehicle && (
                <Box sx={{ minWidth: 0 }}>
                    <Dropdown
                        variant="outlined"
                        size="medium"
                        sx={ffFieldSx}
                        label="Veículo"
                        data={vehicles.map(vehicle => ({
                            id: vehicle.ID || vehicle.Id || vehicle.id,
                            name: `${vehicle.Manufacturer} - ${vehicle.Model}${vehicle.LicensePlate ? ` — ${vehicle.LicensePlate}` : ''}`
                        })) || []}
                        selectedItem={formik.values.vehicle_id}
                        handleChange={(value) => handleVehicleChange({ target: { value } })}
                        error={formik.touched.vehicle_id && Boolean(formik.errors.vehicle_id)}
                        helperText={
                            formik.touched.vehicle_id && formik.errors.vehicle_id
                                ? formik.errors.vehicle_id
                                : !selectedCustomer
                                    ? 'Selecione um cliente para ver os veículos disponíveis'
                                    : ''
                        }
                        disabled={!selectedCustomer}
                    />
                    <NewInlineLink
                        label="Novo veículo"
                        disabled={!selectedCustomer}
                        onClick={() => {
                            if (selectedCustomer) {
                                setOpenDialogNewVehicle(true);
                            }
                        }}
                    />
                </Box>
            )}

            {/* Dialog para novo cliente */}
            <NewCustomer
                open={openDialogNewCustomer}
                setOpen={setOpenDialogNewCustomer}
                onSuccess={(data) => {
                    handleOnSuccess(data);
                    setOpenDialogNewCustomer(false);
                }}
            />

            {/* Dialog para novo veículo */}
            <ModalAdd
                open={openDialogNewVehicle}
                onClose={() => setOpenDialogNewVehicle(false)}
                customerId={selectedCustomer}
                customerName={getCustomerName()}
                onSuccess={handleOnSuccessVehicle}
            />
        </Box>
    );
}
